import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Group, GroupUpdateList, ProductList } from '~/Api/User/ProductGroup/CzReimbursement';
import { updateGroup } from '~/CzReimbursement/actions';
import { EmptyResultReason } from '~/CzReimbursement/EmptyResultReason';
import { selectGroup } from '~/CzReimbursement/reducer';
import { parsePackageSize } from '~/Product';
import ProductGroupList from '~/ProductGroupList';
import { State, ThunkDispatch } from '~/Store';
import ActivityIndicator from '~/UI/ActivityIndicator';

import { EmptySettingsVaslues, Settings, SettingsErrors, SettingsValues } from './Settings';

interface Values {
	[id: string]: SettingsValues;
}

interface Errors {
	[id: string]: SettingsErrors;
}

export const GroupList: React.FunctionComponent<
	React.PropsWithChildren<{
		parentId: string;
		onChange(): void;
	}>
> = ({ parentId, onChange }) => {
	const czReimbursement = useSelector((state: State) => selectGroup(state.czReimbursement, parentId));
	const dispatch = useDispatch<ThunkDispatch<never>>();

	const save = React.useCallback(
		(productLists: GroupUpdateList[]) => {
			dispatch(
				updateGroup(parentId, (group) => ({
					reference: {
						referenceBasketVersion: group.reference.referenceBasketVersion,
						historicProductDataViewDate: group.reference.historicProductDataViewDate,
						possibleSavingsRelativeTo: group.reference.possibleSavingsRelativeTo,
					},
					productLists,
				}))
			);
		},
		[parentId, dispatch]
	);

	if ((czReimbursement?.isFetching ?? false) && czReimbursement?.group === null) {
		return <ActivityIndicator />;
	}

	return czReimbursement !== null && czReimbursement.group !== null ? (
		<List czReimbursementGroup={czReimbursement.group} parentId={parentId} onChange={onChange} onSave={save} />
	) : null;
};

const List: React.FunctionComponent<
	React.PropsWithChildren<{
		czReimbursementGroup: Group;
		parentId: string;
		onChange(): void;
		onSave(lists: GroupUpdateList[]): void;
	}>
> = ({ czReimbursementGroup, parentId, onChange, onSave }) => {
	const [values, setValues] = React.useState(() =>
		czReimbursementGroup.productLists.reduce<Values>((result, list) => {
			result[list.productList.id] = {
				dosageStrength: String(list.dosageStrength ?? ''),
				packageSize: String(list.packageSize ?? ''),
				selected: list.selected,
				usualTherapeuticDailyDose: String(list.usualTherapeuticDailyDose ?? ''),
				usualTherapeuticDailyDosePerPackage: String(list.usualTherapeuticDailyDosePerPackage ?? ''),
			};
			return result;
		}, {})
	);

	const [errors, setErrors] = React.useState<Errors>({});
	const lists = React.useMemo(
		() =>
			czReimbursementGroup.productLists.reduce<{ [id: string]: ProductList }>((result, list) => {
				result[list.productList.id] = list;
				return result;
			}, {}),
		[czReimbursementGroup]
	);

	const updateValues = React.useCallback(
		(id: string, groupValues: SettingsValues) => {
			const newValues = { ...values };
			newValues[id] = groupValues;
			setValues(newValues);

			const [lists, errors] = mapValues(newValues);
			setErrors(errors);

			if (Object.keys(errors).length === 0) {
				onSave(lists);
			}
		},
		[values]
	);

	return (
		<ProductGroupList
			actions={(list, index) => (
				<Settings
					errors={errors[list.id] ?? {}}
					showHeader={index === 0}
					usedForCalculation={
						czReimbursementGroup.finalCalculation.baseReimbursement?.referenceProduct?.productList?.id === list.id
					}
					values={values[list.id] ?? EmptySettingsVaslues}
					onChange={(values) => {
						updateValues(list.id, values);
					}}
				/>
			)}
			itemFooter={(list) => (
				<>
					{lists[list.id]?.emptyResultReason.map((error) => (
						<EmptyResultReason error={error} key={error.code} />
					))}
				</>
			)}
			parentId={parentId}
			onChange={onChange}
		/>
	);
};

const mapValues = (values: Values): [GroupUpdateList[], Errors] => {
	const errors: Errors = {};

	const lists = Object.keys(values).map((id): GroupUpdateList => {
		const groupValues = values[id];
		const dosageStrength = parsePackageSize(groupValues.dosageStrength);
		const packageSize = parsePackageSize(groupValues.packageSize);
		const usualTherapeuticDailyDose = parsePackageSize(groupValues.usualTherapeuticDailyDose);
		const usualTherapeuticDailyDosePerPackage = parsePackageSize(groupValues.usualTherapeuticDailyDosePerPackage);

		const groupErrors: SettingsErrors = {
			dosageStrength: !(dosageStrength.isValid ?? true),
			packageSize: !(packageSize.isValid ?? true),
			usualTherapeuticDailyDose: !(usualTherapeuticDailyDose.isValid ?? true),
			usualTherapeuticDailyDosePerPackage: !(usualTherapeuticDailyDosePerPackage.isValid ?? true),
		};

		if (Object.values(groupErrors).find((value) => value === true)) {
			errors[id] = groupErrors;
		}

		return {
			id,
			dosageStrength: dosageStrength.value,
			packageSize: packageSize.value,
			usualTherapeuticDailyDose: usualTherapeuticDailyDose.value,
			usualTherapeuticDailyDosePerPackage: usualTherapeuticDailyDosePerPackage.value,
			selected: groupValues.selected,
		};
	});

	return [lists, errors];
};
